import React from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { RoomIcon } from "@icarius-icons";
import ScheduleIcon from '@material-ui/icons/Schedule';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import paths from "@icarius-router/paths";
import PositionImageRenderer from "@icarius-table/renderersAndFilters/positionImageRenderer";
import TimeBetweenMarksRenderer from "@icarius-table/renderersAndFilters/timeBetweenMarksRenderer";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import CommonPage from "@icarius-common/commonPage";
import TemplatesHeader from "@icarius-common/templates/components/templatesHeader";
import useTemplates from "@icarius-common/templates/components/useTemplates";
import CreateTemplateDialog from "@icarius-common/templates/components/createTemplateDialog";
import ChangeScheduleDialog from "./dialogs/changeScheduleDialog";
import AssistanceMarkDialog from "./dialogs/assistanceMarkDialog";
import AssistanceMap from "./map";
import useAssistance from "./useAssistance";
import useFixedFilters from "@icarius-common/fixedFilters";

const Assistance = () => {
  const title = getLocalizedString("assistanceTitle");

  const {
    isLoading,
    hasDuplicatedMarks,
    rowData,
    assistanceData,
    openCalendarList,
    employeeList,
    mapData,
    schedules,
    dateFormat,
    locale,
    state,
    handlers,
  } = useAssistance();

  const {
    user,
    templates,
    selectedTemplate,
    isLoadingTemplates,
    createTemplateDialogIsOpen,
    getRole,
    handleCreateTemplate,
    handleImportTemplates,
    handleExportTemplates,
    handleSelectTemplateAndUpdateGrid,
    handleOpenCreateTemplateDialog,
    handleOpenUpdateTemplateDialog,
    handleOpenDeleteTemplateDialog,
    handleCloseCreateTemplateDialog,
  } = useTemplates('ASSISTANCE');

  const {
    hasFixedFilters,
    toggleFixedFilters,
  } = useFixedFilters(title);

  const createAssistanceMarkItem = () =>
    <CustomIconButton
      title={getLocalizedString("createAttendanceMark")}
      type={"add"}
      onClick={handlers.handleOpenCreateAssistanceMark}
    />

  const editAssistanceMarkItem = (gridRef) =>
    <CustomIconButton
      title={getLocalizedString("editAttendanceMark")}
      type={"edit"}
      onClick={() => handlers.handleOpenEditAssistanceMark(gridRef)}
    />

  const changeScheduleItem = (gridRef) =>
    <CustomIconButton
      title={getLocalizedString("changeSchedules")}
      onClick={() => handlers.handleOpenChangeSchedule(gridRef)}
    >
      <ScheduleIcon />
    </CustomIconButton>

  const deleteAssistanceMarkItem = (gridRef) =>
    <CustomIconButton
      title={getLocalizedString("deleteAttendanceMark")}
      type={"delete"}
      onClick={() => handlers.handleDeleteAssistanceMark(gridRef)}
    />

  const deleteDuplicatedAssistanceMarksItem = () => hasDuplicatedMarks &&
    <CustomIconButton
      title={getLocalizedString("deleteDuplicatedAttendanceMarks")}
      onClick={() => handlers.handleDeleteDuplicatedAssistanceMarks()}
    >
      <DeleteSweepIcon />
    </CustomIconButton>

  const openMapItem = (gridRef) => assistanceData.length > 0 &&
    <CustomIconButton
      title={getLocalizedString(state.mapIsOpen ? "closeMap" : "openMap")}
      onClick={() => handlers.handleOpenMap(gridRef)}
    >
      <RoomIcon />
    </CustomIconButton>

  const filtersItem = (gridRef) =>
    <CustomIconButton
      title={hasFixedFilters ? getLocalizedString("removeFixedFilters") : getLocalizedString("setFixedFilters")}
      onClick={() => toggleFixedFilters(gridRef, Boolean(selectedTemplate))}
      type={"fixedFilter"}
      isSelected={hasFixedFilters}
    />

  const child = (gridRef) =>
    <>
      {
        createTemplateDialogIsOpen &&
        <CreateTemplateDialog
          open={createTemplateDialogIsOpen}
          level={user.level}
          handleCreateTemplate={(role, name) => handleCreateTemplate(gridRef, role, name)}
          handleCloseDialog={handleCloseCreateTemplateDialog}
        />
      }
      {
        state.assistanceMarkDialogIsOpen &&
        <AssistanceMarkDialog
          open={state.assistanceMarkDialogIsOpen}
          data={state.selectedData}
          openCalendarList={openCalendarList}
          employeeList={employeeList}
          isLoading={isLoading}
          handleClose={handlers.handleCloseAssistanceMark}
        />
      }
      {
        state.changeScheduleDialogIsOpen &&
        <ChangeScheduleDialog
          open={state.changeScheduleDialogIsOpen}
          isLoading={isLoading}
          day={state.selectedData.day}
          code={state.selectedData.code}
          date={state.selectedData.date}
          schedule={state.selectedData.schedule}
          schedules={schedules}
          handleSubmit={handlers.handleSubmitScheduleChange}
          handleClose={handlers.handleCloseChangeSchedule}
        />
      }
    </>

  const templatesHeader = (gridRef) => (
    <>
      <div style={{ paddingLeft: 10, display: 'flex' }}>
        <TemplatesHeader
          gridRef={gridRef}
          user={user}
          templates={templates}
          selectedTemplate={selectedTemplate}
          getRole={getRole}
          handleImportTemplates={handleImportTemplates}
          handleExportTemplates={handleExportTemplates}
          handleOpenCreateTemplateDialog={handleOpenCreateTemplateDialog}
          handleOpenDeleteTemplateDialog={handleOpenDeleteTemplateDialog}
          handleOpenUpdateTemplateDialog={handleOpenUpdateTemplateDialog}
          handleSelectTemplateAndUpdateGrid={handleSelectTemplateAndUpdateGrid}
        />
      </div>
    </>
  )

  if (state.mapIsOpen) {
    return (
      <AssistanceMap
        isLoading={isLoading || isLoadingTemplates}
        data={mapData}
        handleClose={handlers.handleCloseMap}
      />
    )
  }

  return (
    <CommonPage
      title={title}
      gridTitle={title}
      rowData={rowData}
      locale={locale}
      dateFormat={dateFormat}
      columnDefPage={paths.assistance}
      isLoading={isLoading || isLoadingTemplates}
      menuItems={[createAssistanceMarkItem, editAssistanceMarkItem, changeScheduleItem, deleteAssistanceMarkItem, deleteDuplicatedAssistanceMarksItem, openMapItem, filtersItem]}
      customHeader={templatesHeader}
      frameworkComponents={{ PositionImageRenderer, TimeBetweenMarksRenderer }}
      hasHelp
      hasSelectAll
      hasExpand
      hasSwitch
      handleSwitchClick={handlers.handleToggleShowAllRows}
      switchLabelOff={getLocalizedString("viewOnlyIncompleteMarks")}
      switchLabelOn={getLocalizedString("viewAllMarks")}
      switchValue={state.showAllRows}
      codeFieldName={'CODIGO DE EMPLEADO'}
      hasFixedFilters
    >
      {child}
    </CommonPage>
  );
}

export default Assistance;